import { useState } from 'react';
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';
import { SHIPPING_TYPES } from '@/components/ProductShippingType';

const fmt = (n) => (n == null ? '—' : `$${Number(n).toLocaleString('es-CO')}`);
const EMPTY_LINE = { price: '100000', qty: '1', shipping_price_type: 'seller_rate', custom_shipping_price: '' };

// Simulador de solo lectura: aplica la misma regla del carrito y del checkout.
export const ShippingSimulator = () => {
  const [sellerId, setSellerId] = useState('');
  const [department, setDepartment] = useState('Antioquia');
  const [city, setCity] = useState('Medellín');
  const [lines, setLines] = useState([{ ...EMPTY_LINE }]);
  const [res, setRes] = useState(null);

  const run = async () => {
    try {
      const r = await api.post('/admin/logistics/simulate', {
        seller_id: sellerId.trim() || null, department, city,
        lines: lines.map(l => ({ price: Number(l.price) || 0, qty: Number(l.qty) || 1, shipping_price_type: l.shipping_price_type,
          custom_shipping_price: l.custom_shipping_price === '' ? null : Number(l.custom_shipping_price) })),
      });
      setRes(r.data);
    } catch (e) { toast.error(e.response?.data?.detail?.toString?.() || 'Revisa los datos de la simulación'); }
  };
  const set = (i, k, v) => setLines(lines.map((l, j) => (j === i ? { ...l, [k]: v } : l)));

  return (
    <div data-testid="shipping-simulator">
      <div className="flex flex-wrap gap-2">
        <Input className="rounded-none h-8 w-64 text-sm" placeholder="ID del vendedor (opcional)" value={sellerId} onChange={e => setSellerId(e.target.value)} data-testid="sim-seller-id" />
        <Input className="rounded-none h-8 w-40 text-sm" placeholder="Departamento" value={department} onChange={e => setDepartment(e.target.value)} data-testid="sim-department" />
        <Input className="rounded-none h-8 w-40 text-sm" placeholder="Ciudad" value={city} onChange={e => setCity(e.target.value)} data-testid="sim-city" />
      </div>
      {lines.map((l, i) => (
        <div key={i} className="mt-2 flex flex-wrap items-center gap-2">
          <Input type="number" className="rounded-none h-8 w-32 text-sm" value={l.price} onChange={e => set(i, 'price', e.target.value)} data-testid={`sim-price-${i}`} />
          <Input type="number" className="rounded-none h-8 w-20 text-sm" value={l.qty} onChange={e => set(i, 'qty', e.target.value)} data-testid={`sim-qty-${i}`} />
          <select className="h-8 border border-slate-300 px-2 text-sm" value={l.shipping_price_type} onChange={e => set(i, 'shipping_price_type', e.target.value)} data-testid={`sim-type-${i}`}>
            {SHIPPING_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          {l.shipping_price_type === 'custom' && (
            <Input type="number" className="rounded-none h-8 w-32 text-sm" placeholder="Envío" value={l.custom_shipping_price} onChange={e => set(i, 'custom_shipping_price', e.target.value)} data-testid={`sim-custom-${i}`} />
          )}
          <Button size="sm" variant="ghost" onClick={() => setLines(lines.filter((_, j) => j !== i))} disabled={lines.length === 1} data-testid={`sim-remove-${i}`}><Trash2 className="h-4 w-4" /></Button>
        </div>
      ))}
      <div className="mt-2 flex gap-2">
        <Button size="sm" variant="outline" className="rounded-none" onClick={() => setLines([...lines, { ...EMPTY_LINE }])} data-testid="sim-add-line"><Plus className="mr-1 h-4 w-4" />Producto</Button>
        <Button size="sm" className="rounded-none bg-slate-900" onClick={run} data-testid="sim-run">Simular</Button>
      </div>
      {res && (
        <div className="mt-3 border border-slate-200 bg-slate-50 p-3 text-sm" data-testid="sim-result">
          {res.quote.covered ? (
            <>
              <div>Productos: <b>{fmt(res.products_subtotal)}</b> · Envío: <b data-testid="sim-result-shipping">{fmt(res.quote.amount)}</b> · Total comprador: <b data-testid="sim-result-total">{fmt(res.buyer_total)}</b></div>
              <div className="mt-1 text-xs text-slate-500">
                Regla: {res.quote.mode}{res.quote.free_reason ? ` · gratis por ${res.quote.free_reason}` : ''}
                {res.quote.components ? ` · componentes: ${res.quote.components.map(c => `${c.type} ${fmt(c.amount)}`).join(', ')}` : ''}
              </div>
            </>
          ) : <div className="text-rose-700" data-testid="sim-result-uncovered">Sin cobertura para este destino{res.quote.uncovered_by ? ` (${res.quote.uncovered_by})` : ''}.</div>}
        </div>
      )}
    </div>
  );
};
